import { Check, CornerUpRight, Minus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { PrimaryButton, SecondaryButton, TextButton } from '../components/Button';
import { LiveRegion } from '../components/Screen';
import { StatusChip } from '../components/Status';
import { copy } from '../copy';
import { openExternal, telHref } from '../lib/links';
import { allHandled, currentStop, findStopById } from '../state/route';
import { deliverParcel } from '../state/scanning';
import { UNDO_WINDOW_MS, useAppStore } from '../state/store';

const PLANNING_PHONE: string = import.meta.env.VITE_PLANNING_PHONE ?? '';
const AUTO_RETURN_MS = 2_000;

/** F2–F6 — de vier scanuitkomsten, plus de laadvariant "Deze heb je al geladen". */
export function ScanResultScreen() {
  const outcome = useAppStore((s) => s.scanOutcome);
  const route = useAppStore((s) => s.route);
  const queue = useAppStore((s) => s.queue);
  const purpose = useAppStore((s) => s.scanPurpose);
  const navigate = useAppStore((s) => s.navigate);
  const setScanOutcome = useAppStore((s) => s.setScanOutcome);
  const undo = useAppStore((s) => s.undo);

  const [interacted, setInteracted] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  const backToRide = () => {
    setScanOutcome(null);
    navigate(route && allHandled(route) ? 'return' : 'ride');
  };

  const autoReturn = outcome?.kind === 'delivered' && !outcome.moreParcelsOnStop && !interacted;

  useEffect(() => {
    if (!autoReturn) return;
    const timer = window.setTimeout(backToRide, AUTO_RETURN_MS);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoReturn]);

  // Een geslaagde laadscan krijgt geen eigen scherm; hij hoort in de lijst thuis.
  useEffect(() => {
    if (outcome?.kind !== 'loaded') return;
    setScanOutcome(null);
    navigate('load');
  }, [outcome?.kind, setScanOutcome, navigate]);

  useEffect(() => {
    if (!outcome?.clientActionId) return;
    const started = Date.now();
    const timer = window.setInterval(() => setElapsed(Date.now() - started), 500);
    return () => window.clearInterval(timer);
  }, [outcome?.clientActionId]);

  if (!outcome || outcome.kind === 'loaded') return null;

  const item = outcome.clientActionId
    ? queue.find((candidate) => candidate.clientActionId === outcome.clientActionId)
    : undefined;
  const queued = item !== undefined && item.state !== 'sent';
  const canUndo = item?.state === 'waiting' && elapsed < UNDO_WINDOW_MS;

  const next = route ? currentStop(route) : null;
  const stop = route ? findStopById(route, outcome.stopId) : null;

  const onInteract = () => setInteracted(true);

  const shell = (
    tone: 'mint' | 'amber' | 'terracotta' | 'neutral',
    icon: React.ReactNode,
    title: string,
    body: React.ReactNode,
    foot: React.ReactNode,
  ) => (
    <div
      className={`screen flat flat--${tone}`}
      data-screen="scanresult"
      onPointerDown={onInteract}
      onKeyDown={onInteract}
    >
      <div className="flat__body">
        {icon}
        <h1 className="flat__title">{title}</h1>
        {body}
      </div>
      <LiveRegion assertive>{title}</LiveRegion>
      <div className="flat__foot">{foot}</div>
    </div>
  );

  if (outcome.kind === 'delivered') {
    const label = outcome.moreParcelsOnStop
      ? copy.scanResult.oneMoreParcel
      : next
        ? copy.scanResult.next(next.stopOrder)
        : copy.scanResult.nextReturn;

    return shell(
      'mint',
      <span className="flat__icon flat__icon--circle" aria-hidden>
        <Check size={44} strokeWidth={2} />
      </span>,
      copy.scanResult.delivered,
      <>
        <div className="flat__name">{outcome.partnerName}</div>
        <div className="flat__mono">{outcome.parcelNumber}</div>
        <div className="flat__meta">{copy.scanResult.sentAt(outcome.clock)}</div>
        {queued ? (
          <StatusChip tone="onaccent">{copy.scanResult.queuedChip}</StatusChip>
        ) : null}
      </>,
      <>
        <PrimaryButton
          variant="onaccent"
          onClick={() => {
            setScanOutcome(null);
            if (outcome.moreParcelsOnStop) navigate('scanner');
            else backToRide();
          }}
        >
          {label}
        </PrimaryButton>
        {canUndo ? (
          <TextButton
            variant="onaccent"
            center
            onClick={() => void undo(outcome.clientActionId!).then(backToRide)}
          >
            {copy.scanResult.undo}
          </TextButton>
        ) : null}
      </>,
    );
  }

  if (outcome.kind === 'alreadyLoaded') {
    return shell(
      'neutral',
      <span className="flat__icon flat__icon--ring" aria-hidden>
        <Check size={44} strokeWidth={2} />
      </span>,
      copy.load.alreadyLoaded,
      <>
        <div className="flat__name">{outcome.partnerName}</div>
        <div className="flat__mono">{outcome.parcelNumber}</div>
      </>,
      <PrimaryButton
        onClick={() => {
          setScanOutcome(null);
          navigate('load');
        }}
      >
        {copy.scanResult.back}
      </PrimaryButton>,
    );
  }

  if (outcome.kind === 'alreadyDelivered') {
    return shell(
      'neutral',
      <span className="flat__icon flat__icon--ring" aria-hidden>
        <Check size={44} strokeWidth={2} />
      </span>,
      copy.scanResult.alreadyTitle,
      <>
        <div className="flat__mono">{outcome.parcelNumber}</div>
        <div className="flat__meta">
          {copy.scanResult.alreadyBody(outcome.clock, outcome.partnerName ?? '')}
        </div>
      </>,
      <PrimaryButton onClick={backToRide}>{copy.scanResult.backToRide}</PrimaryButton>,
    );
  }

  if (outcome.kind === 'otherStop') {
    const currentOrder = next?.stopOrder ?? 0;
    const otherOrder = stop?.stopOrder ?? 0;
    return shell(
      'amber',
      <span className="flat__icon flat__icon--square flat__icon--amber" aria-hidden>
        <CornerUpRight size={44} strokeWidth={2} />
      </span>,
      copy.scanResult.otherStopTitle(otherOrder),
      <>
        <div className="flat__name">
          {outcome.partnerName} · {outcome.city}
        </div>
        <div className="flat__mono">{outcome.parcelNumber}</div>
        <div className="flat__meta">{copy.scanResult.otherStopBody(currentOrder, otherOrder)}</div>
      </>,
      <>
        <PrimaryButton
          variant="onaccent"
          onClick={() => {
            if (!stop || !outcome.parcelNumber) return;
            void deliverParcel(stop, outcome.parcelNumber).then(setScanOutcome);
          }}
        >
          {copy.scanResult.otherStopConfirm(otherOrder)}
        </PrimaryButton>
        <SecondaryButton variant="onaccent" onClick={backToRide}>
          {copy.scanResult.back}
        </SecondaryButton>
      </>,
    );
  }

  return shell(
    'terracotta',
    <span className="flat__icon flat__icon--square flat__icon--terracotta" aria-hidden>
      <Minus size={44} strokeWidth={2} />
    </span>,
    copy.scanResult.unknownTitle,
    <>
      <div className="flat__mono">{outcome.code}</div>
      <div className="flat__meta">{copy.scanResult.unknownBody}</div>
    </>,
    <>
      <PrimaryButton
        variant="onaccent"
        onClick={() => {
          setScanOutcome(null);
          navigate(purpose === 'load' ? 'load' : 'stop');
        }}
      >
        {copy.scanResult.back}
      </PrimaryButton>
      <SecondaryButton
        variant="onaccent"
        disabledReason={PLANNING_PHONE ? null : copy.stop.noPhone}
        onClick={() => openExternal(telHref(PLANNING_PHONE))}
      >
        {copy.scanResult.callPlanning}
      </SecondaryButton>
    </>,
  );
}
